import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Breadcrumb from '../components/common/Breadcrumb';
import Image from '../components/common/Image';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { money } from '../utils/format';
import { IconTrash, IconMinus, IconPlus } from '../components/common/Icons';

export default function Cart() {
  const { items, update, remove, subtotal } = useCart();
  const { settings } = useSettings();
  const symbol = settings.shop?.currencySymbol || '$';
  const freeOver = Number(settings.shop?.freeShippingOver || 0);
  const shipping = freeOver && subtotal >= freeOver ? 0 : Number(settings.shop?.shippingFlatRate || 0);

  return (
    <>
      <Seo title="Shopping Cart" noIndex />
      <div className="page-header py-8 md:py-12 text-center"><div className="container-fluid"><h1 className="page-header__title text-3xl md:text-4xl">Shopping Cart</h1></div></div>
      <Breadcrumb items={[{ label: 'Cart' }]} />
      <div className="container-fluid section-my">
        {items.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg text-color-secondary mb-6">Your cart is currently empty.</p>
            <Link to="/collections/all" className="sf__btn sf__btn-primary">Continue shopping</Link>
          </div>
        ) : (
          <div className="flex flex-wrap -mx-4">
            <div className="w-full lg:w-2/3 px-4">
              <table className="eg-cart-table w-full">
                <thead><tr><th className="text-left">Product</th><th>Price</th><th>Quantity</th><th className="text-right">Total</th><th /></tr></thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={`${it.productId}:${it.variantId}`}>
                      <td>
                        <div className="flex items-center">
                          <Link to={`/products/${it.slug}`} className="w-20 flex-shrink-0 mr-4"><Image src={it.image} alt={it.title} ratio={1} /></Link>
                          <div>
                            <Link to={`/products/${it.slug}`} className="font-medium">{it.title}</Link>
                            {it.variantTitle && <div className="text-sm text-color-secondary">{it.variantTitle}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="text-center whitespace-nowrap">{money(it.price, symbol)}</td>
                      <td className="text-center">
                        <div className="eg-qty mx-auto">
                          <button type="button" onClick={() => update(it.productId, it.variantId, it.quantity - 1)} aria-label="Decrease"><IconMinus width={12} height={12} /></button>
                          <input value={it.quantity} onChange={(e) => update(it.productId, it.variantId, Math.max(1, parseInt(e.target.value, 10) || 1))} aria-label="Quantity" />
                          <button type="button" onClick={() => update(it.productId, it.variantId, it.quantity + 1)} aria-label="Increase"><IconPlus width={12} height={12} /></button>
                        </div>
                      </td>
                      <td className="text-right font-medium whitespace-nowrap">{money(it.price * it.quantity, symbol)}</td>
                      <td className="text-right"><button type="button" onClick={() => remove(it.productId, it.variantId)} aria-label="Remove" className="p-2 text-color-secondary hover:text-black"><IconTrash width={16} height={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Link to="/collections/all" className="sf__btn sf__btn-secondary mt-6 inline-block">Continue shopping</Link>
            </div>
            <div className="w-full lg:w-1/3 px-4 mt-10 lg:mt-0">
              <div className="eg-summary">
                <h3 className="text-xl font-medium mb-4">Order summary</h3>
                <div className="flex justify-between py-2 border-b"><span>Subtotal</span><span>{money(subtotal, symbol)}</span></div>
                <div className="flex justify-between py-2 border-b"><span>Shipping</span><span>{shipping === 0 ? 'Free' : money(shipping, symbol)}</span></div>
                <div className="flex justify-between py-3 text-lg font-medium"><span>Total</span><span>{money(subtotal + shipping, symbol)}</span></div>
                {freeOver > 0 && subtotal < freeOver && <p className="text-sm text-color-secondary mb-4">Add {money(freeOver - subtotal, symbol)} more to get free shipping.</p>}
                <Link to="/checkout" className="sf__btn sf__btn-primary w-full block text-center">Proceed to checkout</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
